import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import mediaService from '../api/mediaService';
import axiosInstance from '../api/axiosInstance';
import appInsights from '../services/appInsights';
import '../App.css';
import { useFavicon } from '../useFavicon';

// Define favicons for different media types
const AUDIO_FAVICON_URL = process.env.PUBLIC_URL + "/audio.svg";
const VIDEO_FAVICON_URL = process.env.PUBLIC_URL + "/video.svg";
const MEDIA_FAVICON_URL = process.env.PUBLIC_URL + "/media.svg";

// Create component-specific logger for media viewer
const logger = appInsights.getComponentLogger('MediaViewer');

/**
 * Get a user-friendly error message for a media error code
 * @param {number|string} errorCode - The error code from MediaError
 * @returns {string} A user-friendly error message
 */
const getMediaErrorMessage = (errorCode) => {
  switch (parseInt(errorCode, 10)) {
    case 1:
      return "MEDIA_ERR_ABORTED: Playback was aborted by the user";
    case 2:
      return "MEDIA_ERR_NETWORK: Network error occurred during download. This could be due to network issues or CORS (Cross-Origin Resource Sharing) restrictions.";
    case 3:
      return "MEDIA_ERR_DECODE: The media file is corrupt or uses an unsupported format/codec. Try a different streaming method.";
    case 4:
      return "MEDIA_ERR_SRC_NOT_SUPPORTED: The media format or MIME type is not supported by your browser. Try the 'In-Memory Blob' method which often works around MIME type issues.";
    case 5:
      return "MEDIA_ERR_ENCRYPTED: The media is encrypted and the keys needed for decryption could not be obtained.";
    default:
      return `Unknown error (code ${errorCode}). Try a different streaming method or check the developer console for more information.`;
  }
};

/**
 * Get a recommendation for fixing a specific error
 * @param {number|string} errorCode - The error code from MediaError
 * @returns {string} A recommendation to fix the issue
 */
const getErrorRecommendation = (errorCode) => {
  switch (parseInt(errorCode, 10)) {
    case 2:
      return "Try reloading the page or checking your network connection. The 'In-Memory Blob' method may work better on slower connections.";
    case 3:
      return "Try the 'Direct Stream' method first, then 'In-Memory Blob' if that fails. These provide the most consistent formats.";
    case 4:
      return "The 'In-Memory Blob' method often works best for format compatibility issues. It downloads the file first before playing.";
    default:
      return "Try each streaming method one by one, starting with 'Direct Stream', then 'Buffered Stream', and finally 'In-Memory Blob'.";
  }
};

/**
 * ViewMediaPage - Rebuilt from scratch with simplified reliable media playback
 */
const ViewMediaPage = () => {
  // Route and navigation
  const { id } = useParams();
  const navigate = useNavigate();

  // State variables
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [streamingUrl, setStreamingUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState(id || '');
  const [currentMethod, setCurrentMethod] = useState('binary'); // Initialize to binary by default
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering files
  const [mediaDuration, setMediaDuration] = useState(null); // Store audio/video duration
  const searchInputRef = useRef(null);

  // References
  const mediaRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  
  // Helper function to format duration
  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return 'Unknown';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  };
  
  // Filtered media files based on search term - moved up to fix reference error
  const filteredMediaFiles = mediaFiles.filter(file => {
    const term = searchTerm.toLowerCase();
    return (
      file.name.toLowerCase().includes(term) ||
      file.fileType.toLowerCase().includes(term) ||
      (file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB`.toLowerCase().includes(term) : false) ||
      (file.createdAt ? new Date(file.createdAt).toLocaleString().toLowerCase().includes(term) : false) ||
      (file.description ? file.description.toLowerCase().includes(term) : false) ||
      file.uniqueId.toLowerCase().includes(term)
    );
  });

  // Change favicon based on media type - more specific icons
  const getFaviconForMediaType = (fileType) => {
    if (!fileType) return MEDIA_FAVICON_URL;
    
    if (fileType.toLowerCase().includes('mp3') || fileType.toLowerCase().includes('audio')) {
      return AUDIO_FAVICON_URL;
    } else if (fileType.toLowerCase().includes('mp4') || fileType.toLowerCase().includes('video')) {
      return VIDEO_FAVICON_URL;
    }
    return MEDIA_FAVICON_URL;
  };

  useFavicon(getFaviconForMediaType(mediaFile?.fileType));

  // Fetch all media files on component mount
  useEffect(() => {
    const fetchMediaFiles = async () => {
      try {
        setIsLoading(true);
        const data = await mediaService.getAllMediaFiles();
        setMediaFiles(data);
        
        // If no ID in URL but we have files, select the first one
        // Only do this if we're not in the middle of a search
        if (!id && data.length > 0 && !selectedFileId && !searchTerm) {
          setSelectedFileId(data[0].uniqueId);
        }
      } catch (err) {
        console.error('Error fetching media files:', err);
        setError('Failed to load media files. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMediaFiles();
  }, []); // Remove id from dependencies since we handle it separately

  // When selectedFileId changes, load that file and update URL
  useEffect(() => {
    if (!selectedFileId) return; // Don't do anything if no file is selected
    
    // Update URL without reloading
    navigate(`/view-media/${selectedFileId}`, { replace: true });
    
    // If media file has changed but we already have a file loaded with a different ID,
    // we need to ensure the player is completely reset
    if (mediaFile && mediaFile.uniqueId !== selectedFileId) {
      // Reset streaming URL and stop any playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = '';
      }
      setStreamingUrl('');
      setIsPlaying(false);
    }
    
    let isCurrentRequest = true; // Track if this request is still current
    
    const loadFile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Loading media file with ID:', selectedFileId);
        
        // Fetch file details
        const fileData = await mediaService.getMediaFileById(selectedFileId);
        
        // Check if this request is still current
        if (!isCurrentRequest) return;
        
        console.log('Media file details:', fileData);
        setMediaFile(fileData);
        
        // Reset any existing audio or video players
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        
        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }

        // Set up direct streaming with authentication
        // Use relative path since axiosInstance already has baseURL configured
        const timestamp = Date.now();
        const relativePath = `/media-files/${encodeURIComponent(selectedFileId)}/direct-stream?t=${timestamp}`;
        if (!isCurrentRequest) return;
        
        console.log('Auto-starting authenticated direct streaming URL:', relativePath);
        
        // Use authenticated download for initial load
        axiosInstance.get(relativePath, {
          responseType: 'blob',
          cache: 'no-cache',
          headers: {
            'Accept': '*/*'
          }
        })
          .then(response => {
            if (!isCurrentRequest) return;
            console.log('Initial direct stream response received, content-type:', response.headers['content-type']);
            return response.data;
          })
          .then(blob => {
            if (!isCurrentRequest) return;
            console.log('Initial direct stream blob received, size:', blob.size, 'type:', blob.type);
            
            if (blob.size === 0) {
              throw new Error('Received empty blob from server');
            }
            
            const objectUrl = URL.createObjectURL(blob);
            console.log('Created object URL for initial playback:', objectUrl);
            setStreamingUrl(objectUrl);
            setCurrentMethod('direct');
            
            // Set up auto-play after ensuring DOM is ready
            requestAnimationFrame(() => {
              if (!isCurrentRequest) return;
              
              const player = fileData?.fileType === 'mp3' ? audioRef.current : videoRef.current;
              if (player) {
                player.load();
                // Attempt auto-play
                const playPromise = player.play();
                if (playPromise !== undefined) {
                  playPromise
                    .then(() => {
                      if (!isCurrentRequest) return;
                      console.log('Auto-play started successfully');
                      setIsPlaying(true);
                      setError(null);
                    })
                    .catch(error => {
                      if (!isCurrentRequest) return;
                      console.error('Auto-play failed:', error);
                      const errorCode = player.error ? player.error.code : 'unknown';
                      setError(`Auto-play failed (${errorCode}): ${error.message || 'Unknown error'}. Press Play to start manually.`);
                    });
                }
              }
            });
          })
          .catch(err => {
            if (!isCurrentRequest) return;
            console.error('Error with initial direct streaming:', err);
            setError(`Error loading media file: ${err.message || 'Unknown error'}`);
          });
      } catch (err) {
        if (!isCurrentRequest) return;
        console.error('Error loading media file:', err);
        setError(`Error loading media file: ${err.message || 'Unknown error'}`);
      } finally {
        if (isCurrentRequest) {
          setIsLoading(false);
        }
      }
    };
    
    loadFile();
    
    // Cleanup function to cancel any pending operations when selectedFileId changes
    return () => {
      isCurrentRequest = false;
      
      // Stop and clear both audio and video elements
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = ''; // Clear the source to stop any pending loads
      }
      
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = ''; // Clear the source to stop any pending loads
      }
    };
  }, [selectedFileId, navigate]); // Include navigate in dependencies

  // Add keyboard shortcut to focus search field
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Check if Ctrl+F or Cmd+F was pressed
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        // Prevent the browser's find dialog
        event.preventDefault();
        
        // Focus the search input if it exists
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          
          // Track keyboard shortcut usage
          logger.info('User used keyboard shortcut for search', {
            operation: 'searchShortcut',
            timestamp: new Date().toISOString()
          });
        }
      }
    };
    
    // Add event listener for keydown
    window.addEventListener('keydown', handleKeyDown);
    
    // Clean up the event listener on unmount
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Auto-select a file when there is exactly one search result
  useEffect(() => {
    // Only proceed if there is exactly one match
    if (searchTerm && filteredMediaFiles.length === 1) {
      const matchedFileId = filteredMediaFiles[0].uniqueId;
      
      // Only change the selection if it's different from the current selection
      if (matchedFileId !== selectedFileId) {
        console.log('Auto-selecting the only matching file:', matchedFileId);
        
        // Reset current media state
        setMediaFile(null);
        setStreamingUrl('');
        setIsPlaying(false);
        
        // Select the matched file with a small delay
        setTimeout(() => {
          setSelectedFileId(matchedFileId);
          setError('Loading the only matching file...');
          
          // Log the automatic file selection
          logger.info('Auto-selected only matching file', {
            operation: 'autoFileSelection',
            fileId: matchedFileId,
            searchTerm: searchTerm,
            timestamp: new Date().toISOString()
          });
        }, 50);
      }
    }
  }, [searchTerm, filteredMediaFiles, selectedFileId]);

  // Media control handlers
  const handlePlay = () => {
    const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
    if (player) {
      try {
        logger.info('User initiated media playback', {
          operation: 'user-play',
          fileId: mediaFile?.uniqueId,
          fileType: mediaFile?.fileType,
          currentMethod: currentMethod
        });
        
        console.log('Attempting to play media');
        const playPromise = player.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Media playback started successfully');
              setIsPlaying(true);
              setError(null);
              
              // Track successful playback start
              logger.trackEvent('MediaPlaybackStarted', {
                operation: 'playback-success',
                fileId: mediaFile?.uniqueId,
                fileType: mediaFile?.fileType,
                streamingMethod: currentMethod,
                fileSizeMB: mediaFile?.size ? (mediaFile.size / 1024 / 1024).toFixed(2) : 'unknown'
              });
            })
            .catch(error => {
              console.error('Error playing media:', error);
              setIsPlaying(false);
              
              // Track playback failure
              logger.error('Media playback failed', {
                operation: 'playback-error',
                fileId: mediaFile?.uniqueId,
                fileType: mediaFile?.fileType,
                streamingMethod: currentMethod,
                errorCode: player.error ? player.error.code : 'unknown',
                errorMessage: error.message || 'Unknown error'
              });
              
              // Show detailed error with instructions to try different method manually
              const errorCode = player.error ? player.error.code : 'unknown';
              setError(`Playback failed (code ${errorCode}): ${error.message || 'Unknown error'}. 
                        Please try a different streaming method using the buttons below.`);
              
              // No automatic fallback - user must choose manually
            });
        }
      } catch (err) {
        console.error('Exception during media playback:', err);
        logger.error('Exception during media playback', {
          operation: 'playback-exception',
          fileId: mediaFile?.uniqueId,
          fileType: mediaFile?.fileType,
          streamingMethod: currentMethod,
          error: err.message || 'Unknown error'
        });
        setError(`Playback error: ${err.message || 'Unknown error'}. Please try a different streaming method.`);
      }
    } else {
      logger.error('Media player not available', {
        operation: 'playback-no-player',
        fileId: mediaFile?.uniqueId,
        fileType: mediaFile?.fileType
      });
      setError('Media player not available');
    }
  };

  const handlePause = () => {
    const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
    if (player) {
      logger.info('User paused media playback', {
        operation: 'user-pause',
        fileId: mediaFile?.uniqueId,
        fileType: mediaFile?.fileType,
        currentTime: player.currentTime,
        duration: player.duration
      });
      
      player.pause();
      setIsPlaying(false);
      
      logger.trackEvent('MediaPlaybackPaused', {
        operation: 'playback-paused',
        fileId: mediaFile?.uniqueId,
        fileType: mediaFile?.fileType,
        streamingMethod: currentMethod,
        currentTime: player.currentTime,
        duration: player.duration
      });
    }
  };

  const handleStop = () => {
    const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
    if (player) {
      logger.info('User stopped media playback', {
        operation: 'user-stop',
        fileId: mediaFile?.uniqueId,
        fileType: mediaFile?.fileType,
        currentTime: player.currentTime,
        duration: player.duration
      });
      
      player.pause();
      player.currentTime = 0;
      setIsPlaying(false);
      
      logger.trackEvent('MediaPlaybackStopped', {
        operation: 'playback-stopped',
        fileId: mediaFile?.uniqueId,
        fileType: mediaFile?.fileType,
        streamingMethod: currentMethod
      });
    }
  };

  // Media playback method handlers
  const tryProxyStreaming = () => {
    if (!mediaFile) return;
    
    try {
      // Show loading state immediately
      setError('Setting up buffered streaming... Please wait.');
      setIsPlaying(false);
      
      const proxyUrl = mediaService.getProxyStreamingUrl(mediaFile.uniqueId);
      console.log('Trying proxy streaming URL:', proxyUrl);
      
      // Use authenticated download like the blob method
      axiosInstance.get(proxyUrl, {
        responseType: 'blob',
        cache: 'no-cache',
        headers: {
          'Accept': '*/*'
        }
      })
        .then(response => {
          console.log('Proxy stream response received, content-type:', response.headers['content-type']);
          return response.data;
        })
        .then(blob => {
          console.log('Proxy stream blob received, size:', blob.size, 'type:', blob.type);
          
          if (blob.size === 0) {
            throw new Error('Received empty blob from server');
          }
          
          const objectUrl = URL.createObjectURL(blob);
          console.log('Created object URL for proxy streaming:', objectUrl);
          setStreamingUrl(objectUrl);
          setCurrentMethod('proxy');
          setError('Buffered streaming ready. Press Play to start media.');
          
          setTimeout(() => {
            const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
            if (player) {
              player.load();
              console.log('Player loaded with proxy stream blob URL');
            }
          }, 100);
        })
        .catch(err => {
          console.error('Error with proxy streaming:', err);
          setError(`Error with proxy streaming: ${err.message || 'Unknown error'}. Please try a different streaming method.`);
        });
    } catch (err) {
      console.error('Error setting up proxy streaming:', err);
      setError(`Error setting up proxy streaming: ${err.message || 'Unknown error'}`);
    }
  };
  
  // Auto-fallback function to try next streaming method on error
  const tryNextStreamingMethod = async (errorCode, errorDetails, recommendation) => {
    const streamingMethods = ['direct', 'proxy', 'blob', 'blob-direct'];
    const currentIndex = streamingMethods.indexOf(currentMethod);
    
    if (currentIndex < streamingMethods.length - 1) {
      const nextMethod = streamingMethods[currentIndex + 1];
      console.log(`🔄 Auto-fallback: Trying next streaming method '${nextMethod}' due to error: ${errorDetails}`);
      
      setError(`Auto-switching to ${nextMethod} method due to playback error...`);
      
      try {
        switch (nextMethod) {
          case 'proxy':
            await tryProxyStreaming();
            break;
          case 'blob':
            await tryDirectPlayWithBlob();
            break;
          case 'blob-direct':
            await tryBlobDirectStreaming();
            break;
          default:
            // If we're already at direct, shouldn't happen
            break;
        }
      } catch (fallbackError) {
        console.error(`Fallback method ${nextMethod} also failed:`, fallbackError);
        setError(`${nextMethod} method also failed. ${recommendation}`);
        setIsPlaying(false);
      }
    } else {
      // No more methods to try
      setError(`All streaming methods failed. ${recommendation}`);
      setIsPlaying(false);
    }
  };
  
  const tryDirectStreaming = () => {
    if (!mediaFile) return;
    
    try {
      // Show loading state immediately
      setError('Setting up direct streaming... Please wait.');
      setIsPlaying(false);
      
      const directUrl = mediaService.getDirectStreamingUrl(mediaFile.uniqueId);
      console.log('Trying direct streaming URL:', directUrl);
      
      // Use authenticated download like the blob method
      axiosInstance.get(directUrl, {
        responseType: 'blob',
        cache: 'no-cache',
        headers: {
          'Accept': '*/*'
        }
      })
        .then(response => {
          console.log('Direct stream response received, content-type:', response.headers['content-type']);
          return response.data;
        })
        .then(blob => {
          console.log('Direct stream blob received, size:', blob.size, 'type:', blob.type);
          
          if (blob.size === 0) {
            throw new Error('Received empty blob from server');
          }
          
          const objectUrl = URL.createObjectURL(blob);
          console.log('Created object URL for direct streaming:', objectUrl);
          setStreamingUrl(objectUrl);
          setCurrentMethod('direct');
          setError('Direct streaming ready. Press Play to start media.');
          
          setTimeout(() => {
            const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
            if (player) {
              player.load();
              console.log('Player loaded with direct stream blob URL');
            }
          }, 100);
        })
        .catch(err => {
          console.error('Error with direct streaming:', err);
          setError(`Error with direct streaming: ${err.message || 'Unknown error'}. Please try a different streaming method.`);
        });
    } catch (err) {
      console.error('Error setting up direct streaming:', err);
      setError(`Error setting up direct streaming: ${err.message || 'Unknown error'}`);
    }
  };
  
  const tryDirectPlayWithBlob = () => {
    if (!mediaFile) return;
    
    try {
      // Show loading state immediately 
      setError('Downloading file for in-memory playback... Please wait, this may take a moment.');
      setIsPlaying(false);
      
      const downloadUrl = mediaService.getDownloadUrl(mediaFile.uniqueId);
      console.log('Trying direct play with download URL:', downloadUrl);
      
      axiosInstance.get(downloadUrl, {
        responseType: 'blob',
        cache: 'no-cache',
        headers: {
          'Accept': '*/*'
        }
      })
        .then(response => {
          console.log('Download response received, content-type:', response.headers['content-type']);
          console.log('Download response received, content-length:', response.headers['content-length']);
          setError('File downloading... Please wait.');
          return response.data; // axiosInstance returns blob in response.data
        })
        .then(blob => {
          console.log('Blob received, size:', blob.size, 'type:', blob.type);
          
          if (blob.size === 0) {
            throw new Error('Received empty blob from server');
          }
          
          const objectUrl = URL.createObjectURL(blob);
          console.log('Created object URL for playback:', objectUrl);
          setStreamingUrl(objectUrl);
          setCurrentMethod('blob');
          setError('In-memory playback ready. Press Play to start media.');
          
          setTimeout(() => {
            const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
            if (player) {
              player.load();
              console.log('Player loaded with blob URL');
              
              // Don't auto-play, let user press play button manually
              // This avoids auto-cycling between methods if errors occur
            } else {
              console.error('Player reference not available');
            }
          }, 100);
        })
        .catch(err => {
          console.error('Error with direct play:', err);
          setError(`Error downloading file: ${err.message || 'Unknown error'}. Please try a different streaming method.`);
        });
    } catch (err) {
      console.error('Error setting up direct play:', err);
      setError(`Error setting up direct play: ${err.message || 'Unknown error'}. Please try a different streaming method.`);
    }
  };

  const tryBlobDirectStreaming = async () => {
    if (!mediaFile) return;
    
    try {
      // Show loading state immediately
      setError('Setting up blob-direct streaming... Please wait.');
      setIsPlaying(false);
      
      console.log('Attempting blob-direct streaming for media file:', mediaFile.uniqueId);
      
      // Get the direct blob URL from the backend
      const blobData = await mediaService.getBlobDirectStreamingUrl(mediaFile.uniqueId);
      console.log('Blob-direct streaming URL obtained:', blobData.streamingUrl);
      
      setStreamingUrl(blobData.streamingUrl);
      setCurrentMethod('blob-direct');
      
      setTimeout(() => {
        const player = mediaFile?.fileType === 'mp3' ? audioRef.current : videoRef.current;
        if (player) {
          player.load();
          setError('Blob-direct streaming ready. Press Play to start media.');
          
          // Don't auto-play, let user press play button manually
          // This avoids auto-cycling between methods if errors occur
        }
      }, 100);
    } catch (err) {
      console.error('Error setting up blob-direct streaming:', err);
      setError(`Error setting up blob-direct streaming: ${err.message || 'Unknown error'}`);
    }
  };

  // File selection handler
  const handleFileSelectChange = (e) => {
    const newFileId = e.target.value;
    
    // Log the file selection change
    logger.info('User changed selected media file', {
      operation: 'fileSelection',
      previousFileId: selectedFileId,
      newFileId: newFileId,
      timestamp: new Date().toISOString(),
      fromSearch: searchTerm ? true : false
    });
    
    // Stop any currently playing media (both audio and video)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = ''; // Clear the source to stop any pending loads
    }
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = ''; // Clear the source to stop any pending loads
    }
    
    // Reset player state
    setIsPlaying(false);
    setError(null);
    
    // Always force a complete reload of the media file
    // First, fully reset the current media state
    setMediaFile(null);
    setStreamingUrl('');
    
    // A small delay helps ensure React has processed the state reset
    setTimeout(() => {
      // Now set the new file ID to trigger the useEffect
      setSelectedFileId(newFileId);
      setError('Loading selected media file...');
      
      console.log('File selection complete, loading file:', newFileId);
    }, 50);
  };

  // Navigation handlers
  const handleBackToFiles = () => {
    navigate('/media-files');
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  // Download QR code for the current media file
  const handleDownloadQRCode = async () => {
    if (!mediaFile) return;
    
    try {
      await mediaService.downloadQRCode(mediaFile.uniqueId, mediaFile.name);
      
      // Track successful QR code download
      logger.info('QR code download completed from ViewMediaPage', {
        operation: 'qrCodeDownload',
        fileId: mediaFile.uniqueId,
        fileName: mediaFile.name,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      setError(`Error downloading QR code: ${error.message}`);
      
      // Track QR code download failure
      logger.error('QR code download failed from ViewMediaPage', {
        operation: 'qrCodeDownload',
        fileId: mediaFile.uniqueId,
        fileName: mediaFile.name,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Get formatted file type
  const getFileType = (type) => {
    return type === 'mp3' ? 'Audio' : 'Video';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatShortDate = (value) => {
    if (!value) return 'Unknown date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown date';
    return date.toLocaleDateString();
  };

  const formatMediaOptionLabel = (file) => {
    const parts = [
      file.name,
      getFileType(file.fileType),
      formatFileSize(file.fileSize),
      formatShortDate(file.createdAt)
    ];

    return parts.join(' • ');
  };
  
  // Highlight matched text in a string
  const highlightMatchedText = (text, query) => {
    if (!query) return text;
    
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    
    if (!lowerText.includes(lowerQuery)) return text;
    
    const startIndex = lowerText.indexOf(lowerQuery);
    const endIndex = startIndex + lowerQuery.length;
    
    return (
      <>
        {text.substring(0, startIndex)}
        <span style={{ 
          backgroundColor: '#FFFF00', 
          fontWeight: 'bold',
          padding: '0 2px'
        }}>
          {text.substring(startIndex, endIndex)}
        </span>
        {text.substring(endIndex)}
      </>
    );
  };

  // Render method badges based on current streaming method
  const renderMethodBadge = () => {
    if (!currentMethod) return null;
    
    let color, text;
    
    switch (currentMethod) {
      case 'binary':
      case 'direct':
        color = '#007bff';
        text = 'Direct Stream';
        break;
      case 'proxy':
        color = '#28a745';
        text = 'Buffered Stream';
        break;
      case 'blob':
        color = '#dc3545';
        text = 'In-Memory Blob';
        break;
      case 'blob-direct':
        color = '#17a2b8';
        text = 'Blob-Direct Stream';
        break;
      default:
        color = '#6c757d';
        text = 'Unknown';
    }
    
    return (
      <div style={{
        display: 'inline-block',
        backgroundColor: color,
        color: 'white',
        padding: '5px 10px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: 'bold',
        marginLeft: '10px'
      }}>
        {text}
      </div>
    );
  };

  const sectionCardStyle = {
    padding: '24px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
    marginBottom: '20px'
  };

  const sectionTitleStyle = {
    marginTop: 0,
    marginBottom: '8px',
    fontSize: '22px',
    color: '#0f172a'
  };

  const sectionDescriptionStyle = {
    marginTop: 0,
    marginBottom: '18px',
    color: '#475569',
    lineHeight: 1.6
  };

  const metadataPillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px 12px',
    borderRadius: '999px',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    fontSize: '13px',
    fontWeight: 600,
    marginRight: '10px',
    marginBottom: '10px'
  };

  return (
    <div className="page-container">
      <div style={{
        marginBottom: '24px',
        padding: '28px',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 55%, #fefce8 100%)',
        border: '1px solid #dbeafe'
      }}>
        <h1 style={{ marginTop: 0, marginBottom: '10px', color: '#0f172a' }}>View Media</h1>
        <p style={{ margin: 0, maxWidth: '760px', color: '#334155', lineHeight: 1.7 }}>
          This page is meant for direct-linked playback from QR codes in a book. The main focus stays on the current video or audio item first, with browsing and troubleshooting controls moved below it.
        </p>
      </div>
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="loading-indicator" style={{
          padding: '15px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <div className="spinner" style={{
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '3px solid rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
            borderTopColor: '#007bff',
            animation: 'spin 1s linear infinite',
            marginRight: '10px'
          }}></div>
          Loading media file...
        </div>
      )}

      {!isLoading && mediaFile && (
        <div style={sectionCardStyle}>
          <div style={{ marginBottom: '18px' }}>
            <div style={{
              fontSize: '12px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#64748b',
              fontWeight: 700,
              marginBottom: '10px'
            }}>
              Now Playing
            </div>
            <h2 style={{ marginTop: 0, marginBottom: '10px', fontSize: '32px', color: '#0f172a' }}>
              {mediaFile.name}
            </h2>
            {mediaFile.description && (
              <p style={{ marginTop: 0, marginBottom: '14px', color: '#475569', lineHeight: 1.7 }}>
                {mediaFile.description}
              </p>
            )}
            <div>
              <span style={metadataPillStyle}>{getFileType(mediaFile.fileType)}</span>
              {mediaDuration && <span style={metadataPillStyle}>Duration: {formatDuration(mediaDuration)}</span>}
              {mediaFile.fileSize && <span style={metadataPillStyle}>{(mediaFile.fileSize / 1024 / 1024).toFixed(2)} MB</span>}
              {renderMethodBadge()}
            </div>
          </div>
        </div>
      )}
      
      {/* Media player */}
      {!isLoading && mediaFile && streamingUrl && (
        <div className="media-player-wrapper" style={{
          marginBottom: '20px',
          backgroundColor: '#000',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
        }}>
          {mediaFile.fileType === 'mp3' ? (
            // Audio player
            <div className="audio-container" style={{
              padding: '20px',
              backgroundColor: '#333',
              borderRadius: '8px'
            }}>
              <audio 
                ref={audioRef}
                src={streamingUrl}
                controls
                autoPlay
                style={{width: '100%'}}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onLoadedMetadata={(e) => {
                  if (e.target.duration && !isNaN(e.target.duration)) {
                    setMediaDuration(e.target.duration);
                  }
                }}
                onError={(e) => {
                  console.error('Audio error:', e.target.error);
                  const errorCode = e.target.error ? e.target.error.code : 'unknown';
                  const errorDetails = getMediaErrorMessage(errorCode);
                  const recommendation = getErrorRecommendation(errorCode);
                  
                  console.log(`Audio error code: ${errorCode} - ${errorDetails}`);
                  console.log(`Recommendation: ${recommendation}`);
                  
                  // Auto-fallback to next streaming method
                  tryNextStreamingMethod(errorCode, errorDetails, recommendation);
                }}
              />
            </div>
          ) : (
            // Video player
            <div className="video-container">
              <video
                ref={videoRef}
                src={streamingUrl}
                controls
                autoPlay
                style={{width: '100%', maxHeight: '500px'}}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onLoadedMetadata={(e) => {
                  if (e.target.duration && !isNaN(e.target.duration)) {
                    setMediaDuration(e.target.duration);
                  }
                }}
                onError={(e) => {
                  console.error('Video error:', e.target.error);
                  const errorCode = e.target.error ? e.target.error.code : 'unknown';
                  const errorDetails = getMediaErrorMessage(errorCode);
                  const recommendation = getErrorRecommendation(errorCode);
                  
                  console.log(`Video error code: ${errorCode} - ${errorDetails}`);
                  console.log(`Recommendation: ${recommendation}`);
                  
                  // Auto-fallback to next streaming method
                  tryNextStreamingMethod(errorCode, errorDetails, recommendation);
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Search and file selection moved below the player */}
      {!isLoading && mediaFiles.length > 0 && (
        <div style={sectionCardStyle}>
          <h2 style={sectionTitleStyle}>Find Another Media File</h2>
          <p style={sectionDescriptionStyle}>
            Use search to jump to a different linked recording, then choose it from the list below.
          </p>

          <div className="search-container" style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ position: 'absolute', left: '10px', color: '#666', zIndex: 1 }}>🔍</div>
              <input 
                ref={searchInputRef}
                type="text"
                placeholder="Search media files by name, type, size, or description... (Ctrl+F)"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  logger.info('User searching media files', {
                    operation: 'searchFiles',
                    searchLength: e.target.value.length,
                    timestamp: new Date().toISOString()
                  });
                }}
                style={{
                  padding: '12px 14px 12px 36px',
                  borderRadius: '10px',
                  border: '1px solid #cbd5e1',
                  boxShadow: searchTerm ? '0 0 0 3px rgba(59,130,246,0.15)' : 'none',
                  width: '100%',
                  maxWidth: '560px',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#fff'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    logger.info('User cleared search', {
                      operation: 'clearSearch',
                      timestamp: new Date().toISOString()
                    });
                  }}
                  style={{
                    padding: '11px 14px',
                    backgroundColor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {searchTerm && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#475569' }}>
                <div>{filteredMediaFiles.length} of {mediaFiles.length} files match your search</div>
                {filteredMediaFiles.length > 0 && (
                  <div style={{ fontSize: '13px', marginTop: '4px', color: '#005cbf', fontStyle: 'italic' }}>
                    Pick one from the dropdown to replace the current item.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="media-selector" style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            marginBottom: '10px',
            flexDirection: 'column'
          }}>
            <label htmlFor="mediaSelect" style={{ fontWeight: 'bold', color: '#0f172a' }}>
              Select Media File
            </label>
            <select 
              id="mediaSelect" 
              value={selectedFileId} 
              onChange={handleFileSelectChange}
              disabled={isLoading || filteredMediaFiles.length === 0}
              style={{
                padding: '12px 14px',
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                minWidth: '300px',
                width: '100%',
                maxWidth: '680px',
                backgroundColor: '#fff',
                fontSize: '15px'
              }}
            >
              <option value="" disabled>Select a file</option>
              {filteredMediaFiles.map((file) => (
                <option key={file.uniqueId} value={file.uniqueId}>
                  {formatMediaOptionLabel(file)}
                </option>
              ))}
            </select>
            {mediaFile && (
              <div style={{ fontSize: '13px', color: '#64748b', lineHeight: 1.6, maxWidth: '680px' }}>
                Current selection: {getFileType(mediaFile.fileType)} • {formatFileSize(mediaFile.fileSize)} • ID {mediaFile.uniqueId}
              </div>
            )}
          </div>

          {searchTerm && filteredMediaFiles.length === 0 && (
            <div className="no-results" style={{ 
              padding: '18px', 
              backgroundColor: '#f8fafc', 
              borderRadius: '12px',
              marginTop: '12px',
              textAlign: 'center',
              border: '1px solid #e2e8f0'
            }}>
              <p style={{ fontSize: '16px', marginBottom: '10px' }}>No files match your search: <strong>"{searchTerm}"</strong></p>
              <button 
                onClick={() => setSearchTerm('')}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer'
                }}
              >
                Clear Search
              </button>
            </div>
          )}
        </div>
      )}

      {/* File information */}
      {mediaFile && (
        <div className="media-info-container" style={{
          ...sectionCardStyle,
          backgroundColor: '#f8fafc'
        }}>
          <h2 style={sectionTitleStyle}>Media Information</h2>
          <p style={sectionDescriptionStyle}>
            Quick reference details for the file currently opened from the book link or from the selector below.
          </p>
          <div style={{display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px'}}>
            <div style={{fontWeight: 'bold'}}>Name:</div>
            <div>{mediaFile.name}</div>
            <div style={{fontWeight: 'bold'}}>Type:</div>
            <div>{getFileType(mediaFile.fileType)}</div>
            <div style={{fontWeight: 'bold'}}>Content Type:</div>
            <div>{mediaFile.contentType || 'Unknown'}</div>
            <div style={{fontWeight: 'bold'}}>File Size:</div>
            <div>{mediaFile.fileSize ? `${(mediaFile.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}</div>
            {mediaDuration && (
              <>
                <div style={{fontWeight: 'bold'}}>Duration:</div>
                <div>{formatDuration(mediaDuration)}</div>
              </>
            )}
            <div style={{fontWeight: 'bold'}}>File ID:</div>
            <div>{mediaFile.uniqueId}</div>
          </div>
        </div>
      )}

      {/* Streaming methods as a separate lower section */}
      {mediaFile && (
        <div style={sectionCardStyle}>
          <h2 style={sectionTitleStyle}>Streaming Methods</h2>
          <p style={sectionDescriptionStyle}>
            If playback fails on one method, switch to another without leaving the page.
          </p>
          <div className="streaming-method-controls" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '10px',
            marginBottom: '8px'
          }}>
            {/* Direct Stream Button - Uses the /direct-stream endpoint */}
              <button
                onClick={tryDirectStreaming}
                disabled={currentMethod === 'direct' || currentMethod === 'binary'}
                style={{
                  padding: '8px 12px',
                  backgroundColor: (currentMethod === 'direct' || currentMethod === 'binary') ? '#007bff' : '#f0f0f0',
                  color: (currentMethod === 'direct' || currentMethod === 'binary') ? 'white' : 'black',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>Direct Stream</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>
                  Fast streaming from Azure
                </div>
              </button>

            {/* Proxy Stream Button */}
            <button
                onClick={tryProxyStreaming}
                disabled={currentMethod === 'proxy'}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentMethod === 'proxy' ? '#28a745' : '#f0f0f0',
                  color: currentMethod === 'proxy' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>Buffered Stream</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>
                  Good for slower connections
                </div>
              </button>

            {/* In-Memory Blob Button */}
            <button
                onClick={tryDirectPlayWithBlob}
                disabled={currentMethod === 'blob'}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentMethod === 'blob' ? '#dc3545' : '#f0f0f0',
                  color: currentMethod === 'blob' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>In-Memory Blob</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>
                  Best compatibility, slower
                </div>
              </button>

            {/* Blob-Direct Stream Button */}
            <button
                onClick={tryBlobDirectStreaming}
                disabled={currentMethod === 'blob-direct'}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentMethod === 'blob-direct' ? '#17a2b8' : '#f0f0f0',
                  color: currentMethod === 'blob-direct' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  textAlign: 'left'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>Blob-Direct Stream</div>
                <div style={{ fontSize: '11px', opacity: 0.8 }}>
                  Fastest for supported files
                </div>
              </button>
          </div>
          {currentMethod && (
            <div style={{ 
              marginTop: '12px',
              padding: '10px 14px',
              backgroundColor: '#f8fafc',
              borderRadius: '10px',
              fontSize: '14px',
              textAlign: 'center',
              border: '1px solid #e2e8f0'
            }}>
              Current method: <strong>{currentMethod}</strong>
            </div>
          )}
        </div>
      )}

      {/* No files message */}
      {!isLoading && !error && mediaFiles.length === 0 && (
        <div className="no-files-message" style={{
          padding: '40px 20px',
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <h3>No Media Files Available</h3>
          <p>Please upload some files first.</p>
          <button 
            onClick={handleBackToFiles}
            style={{
              padding: '10px 15px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Go to Media Files
          </button>
        </div>
      )}
      
      {/* Navigation buttons */}
      <div className="navigation-buttons" style={{
        display: 'flex',
        gap: '15px',
        marginTop: '30px'
      }}>
        <button 
          onClick={handleBackToFiles}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            marginRight: '10px'
          }}
        >
          ← Back to Media Files
        </button>
        
        <button 
          onClick={handleDownloadQRCode}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            marginRight: '10px'
          }}
          title="Download QR code for this media file"
        >
          📱 Download QR Code
        </button>
        
        <button 
          onClick={handleBackToMain}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Back to Main Page
        </button>
      </div>
    </div>
  );
};

export default ViewMediaPage;
